import { ICoreResponse } from "@/core/interfaces/coreResponse.interface";
import { StatusCodes } from "http-status-codes";
import { ActionContext, ActionTree } from "vuex";
import { ISOAState } from "@/core/interfaces/soaState.interface";
import SOAService from "@/modules/SOA/services/soa.service";
import {
  NOTIFICATION_EVENTS,
  notificationBus,
} from "@/core/eventBusses/notification";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import {
  SOAFetchActionModel,
  GETSOAActionModel,
} from "@/modules/SOA/models/soaFetchAction.model";
import { formatString } from "@/core/utils/helpers";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import dowload from "downloadjs";
import { StoreState } from "@/store/type";

const actions: ActionTree<ISOAState, StoreState> = {
  async fetchSOA(
    context: ActionContext<ISOAState, StoreState>,
    payload: GETSOAActionModel,
  ) {
    const { params, fileInfo } = payload;
    const fileName = `${fileInfo.customerName}_(${fileInfo.startDate})_(${fileInfo.endDate}).pdf`;
    try {
      context.commit(
        params.send_email ? "enableSendingSOA" : "enableDownloadingSOA",
      );
      const soaService = new SOAService();
      const response: ICoreResponse = await soaService.get(params);
      if (response.status === StatusCodes.OK) {
        if (!params.send_email) {
          dowload(response.data, fileName);
        }

        notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, {
          type: NOTIFICATION_TYPES.SUCCESS,
          description: params.send_email
            ? formatString(
                NOTIFICATION_MESSAGES.SOA_SEND_EMAIL_SUCCESS,
                fileInfo.customerName || "",
              )
            : formatString(
                NOTIFICATION_MESSAGES.SOA_DOWNLOAD_SUCCESS,
                fileInfo.customerName || "",
              ),
        });
      } else {
        throw response;
      }
    } catch (err) {
      notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, {
        type: NOTIFICATION_TYPES.ERROR,
        description: params.send_email
          ? NOTIFICATION_MESSAGES.SOA_SEND_EMAIL_ERROR
          : NOTIFICATION_MESSAGES.SOA_DOWNLOAD_ERROR,
      });
    }
    context.commit(
      params.send_email ? "disableSendingSOA" : "disableDownloadingSOA",
    );
  },
  async downloadSOA(
    context: ActionContext<ISOAState, StoreState>,
    payload: GETSOAActionModel,
  ) {
    const { params, fileInfo } = payload;
    const fileName = `${fileInfo.customerName}_(${fileInfo.startDate})_(${fileInfo.endDate}).pdf`;
    try {
      context.commit(
        params.send_email ? "enableSendingSOA" : "enableDownloadingSOA",
      );
      const soaService = new SOAService();
      const response: ICoreResponse = await soaService.get(params);
      if (response.status === StatusCodes.OK && response.success) {
        if (!params.send_email) {
          dowload(response.data, fileName);
        }

        notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, {
          type: NOTIFICATION_TYPES.SUCCESS,
          description: formatString(
            NOTIFICATION_MESSAGES.SOA_DOWNLOAD_SUCCESS,
            fileInfo.customerName || "",
          ),
        });
      } else {
        throw response;
      }
    } catch (err) {
      notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, {
        type: NOTIFICATION_TYPES.ERROR,
        description: params.send_email
          ? NOTIFICATION_MESSAGES.SOA_SEND_EMAIL_ERROR
          : NOTIFICATION_MESSAGES.SOA_DOWNLOAD_ERROR,
      });
    }
    context.commit(
      params.send_email ? "disableSendingSOA" : "disableDownloadingSOA",
    );
  },
  async sendSOAEmail(
    context: ActionContext<ISOAState, StoreState>,
    payload: SOAFetchActionModel,
  ) {
    const { requestPayload, fileInfo } = payload;
    const fileName = `${fileInfo?.customerName}_(${fileInfo.startDate})_(${fileInfo.endDate}).pdf`;
    try {
      context.commit(
        requestPayload.send_email ? "enableSendingSOA" : "enableDownloadingSOA",
      );
      const soaService = new SOAService();
      const response: ICoreResponse<any> =
        await soaService.sendSOAEmail(requestPayload);
      if (response.status === StatusCodes.OK) {
        if (requestPayload.send_email === false) {
          dowload(response.data, fileName);
        }
        notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, {
          type: NOTIFICATION_TYPES.SUCCESS,
          description: response?.message,
        });
      } else {
        throw response;
      }
    } catch (err: any) {
      notificationBus.emit(NOTIFICATION_EVENTS.TRIGGER, {
        type: NOTIFICATION_TYPES.ERROR,
        description: err?.message || "",
      });
    }
    context.commit(
      requestPayload.send_email ? "disableSendingSOA" : "disableDownloadingSOA",
    );
  },

  async fetchPendingReceiptAndInvoice(
    context: ActionContext<ISOAState, StoreState>,
    { id }: { id: string },
  ) {
    try {
      const soaService = new SOAService();
      const response: ICoreResponse = await soaService.getPendingInvoice(id);
      if (response.success && response.status === StatusCodes.OK) {
        const {
          invoices,
          receipts,
          hotel_invoices: hotelInvoices,
          group_flight_invoice: groupFlightInvoices,
        } = response.data;
        context.commit("setPendingData", {
          invoices,
          receipts,
          hotelInvoices,
          groupFlightInvoices,
        });
      }
    } catch (err) {
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        "Error while trying to fetch pending invoices";
      notificationService.triggerNotification();
    }
  },
};

export default actions;
